import { CONTENT_MAX_WIDTH } from "@/shared/constants/layout.constants";
import { SIDEBAR_WIDTH_EXPANDED_PX } from "@/components/layout/sidebarLayout.const";

/** Target cards per page before snapping to a multiple of the current column count. */
export const PREFERRED_COMPANY_GRID_PAGE_SIZE = 12;

/** Debounce grid-width reads so sidebar/window resize does not spam fetches. */
export const COMPANY_GRID_WIDTH_DEBOUNCE_MS = 200;

/**
 * Container-query breakpoints (keys are the grid's own available width, not the viewport).
 * This keeps each card at a readable minimum width (~235px) and wraps the remaining cards
 * to the next line when the available space (e.g. with the sidebar expanded) is small.
 * Up to 5 cards per row on a wide container.
 * NOTE: consumers must render the grid with `type="container"`.
 */
export const COMPACT_COMPANY_GRID_COLS = {
  base: 1,
  "480px": 2,
  "720px": 3,
  "960px": 4,
  "1200px": 5,
} as const;

const COL_BREAKPOINTS: { minWidth: number; columns: number }[] = [
  { minWidth: 1200, columns: COMPACT_COMPANY_GRID_COLS["1200px"] },
  { minWidth: 960, columns: COMPACT_COMPANY_GRID_COLS["960px"] },
  { minWidth: 720, columns: COMPACT_COMPANY_GRID_COLS["720px"] },
  { minWidth: 480, columns: COMPACT_COMPANY_GRID_COLS["480px"] },
];

/**
 * Fallback when the grid container has not been measured yet (first paint, bfcache restore).
 * Approximates the content column so JS row-splitting matches CSS container-query columns.
 */
export function estimateCompanyGridContainerWidth(): number {
  if (typeof window === "undefined") return CONTENT_MAX_WIDTH;
  const vw = window.innerWidth;
  const paddingPx = vw >= 768 ? 48 : 32;
  const sidebarPx = vw >= 768 ? SIDEBAR_WIDTH_EXPANDED_PX : 0;
  const available = vw - sidebarPx - paddingPx;
  return Math.min(CONTENT_MAX_WIDTH, Math.max(320, available));
}

/** Column count for the compact company grid (container width, same as SimpleGrid container queries). */
export function companyGridColumnCount(widthPx: number): number {
  const width =
    Number.isFinite(widthPx) && widthPx > 0 ? widthPx : estimateCompanyGridContainerWidth();
  for (const bp of COL_BREAKPOINTS) {
    if (width >= bp.minWidth) return bp.columns;
  }
  return COMPACT_COMPANY_GRID_COLS.base;
}

/** Snap a preferred page size to a multiple of `columns` (complete rows). */
export function pageSizeForCompanyGrid(
  columns: number,
  preferred = PREFERRED_COMPANY_GRID_PAGE_SIZE,
): number {
  const cols = Math.max(1, Math.floor(columns) || 1);
  const pref = Math.max(cols, preferred);
  return Math.max(cols, Math.round(pref / cols) * cols);
}

/**
 * Dropdown sizes that fill complete rows.
 * ~2–5 rows, capped near 20 cards (5-col stops at 4 rows).
 */
export function pageSizeOptionsForColumns(columns: number): number[] {
  const cols = Math.max(1, Math.floor(columns) || 1);
  if (cols === 1) return [8, 10, 12];
  const minRows = cols <= 2 ? 3 : 2;
  const maxRows = cols >= 5 ? 4 : cols <= 2 ? 6 : 5;
  const options: number[] = [];
  for (let rows = minRows; rows <= maxRows; rows++) {
    options.push(cols * rows);
  }
  return options;
}

/** Keep the first item of the current page visible after a page-size change. */
export function remapPageIndex(page: number, oldSize: number, newSize: number): number {
  if (oldSize <= 0 || newSize <= 0 || oldSize === newSize) return Math.max(1, page);
  return Math.floor(((Math.max(1, page) - 1) * oldSize) / newSize) + 1;
}
